import { useTranslation } from "react-i18next";

import { Pill } from "@/components/ui/Pill";
import { formatQuantity } from "@/lib/displayFormat";

/**
 * Carbon footprint badge (PR-63).
 *
 * Reads ``order.carbon_footprint_gco2e`` (g CO2e — already converted
 * from the BE's mg/kg storage). Renders nothing when the value is
 * absent so we don't surface a "0 g CO2e" sticker for orders placed
 * before the snapshotter ran.
 */
export function CarbonBadge({
  gco2e,
}: {
  gco2e: number | null | undefined;
}): JSX.Element | null {
  const { t, i18n } = useTranslation("order");
  if (gco2e === null || gco2e === undefined) return null;

  const display =
    gco2e >= 1_000
      ? formatQuantity(gco2e / 1_000, "kg", { locale: i18n.language, maxFractionDigits: 1 })
      : `${gco2e} g`;

  return (
    <Pill tone="ok" data-testid="carbon-score-badge">
      {t("carbonBadge", { value: display, defaultValue: `CO₂e ${display}` })}
    </Pill>
  );
}
